
import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from "@/components/ui/checkbox";
import { saveTransferToSupabase } from '@/lib/supabase';
import PageLayout from '@/components/PageLayout';
import { useTranslation } from 'react-i18next';
import { formatUssdTelUrl } from '@/utils/ussd-utils';

const TransferCredit = () => {
  const { t } = useTranslation();
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [savedTransferCode, setSavedTransferCode] = useState<string>("");

  // Load saved transfer code on component mount
  useEffect(() => {
    const savedCode = localStorage.getItem('savedTransferCode');
    if (savedCode) {
      setSavedTransferCode(savedCode);
    }
  }, []);

  // Transfer credit validation schema
  const transferFormSchema = z.object({
    transferCode: z.string().min(4, t('tools.validation.codeRequired')),
    amount: z.string().min(1, t('tools.validation.amountRequired')).regex(/^\d+$/, t('tools.validation.amountFormat')),
    recipientNumber: z.string()
      .min(8, t('tools.validation.phoneLength'))
      .regex(/^77\d{6}$/, t('tools.validation.phoneFormat')),
    rememberPassword: z.boolean().default(false)
  });

  const transferForm = useForm<z.infer<typeof transferFormSchema>>({
    resolver: zodResolver(transferFormSchema),
    defaultValues: {
      transferCode: savedTransferCode,
      amount: "",
      recipientNumber: "",
      rememberPassword: false
    }
  });

  const onTransferSubmit = async (data: z.infer<typeof transferFormSchema>) => {
    // Save the transfer code if "remember password" is checked
    if (data.rememberPassword) {
      localStorage.setItem('savedTransferCode', data.transferCode);
      setSavedTransferCode(data.transferCode);
    } else if (!data.rememberPassword && localStorage.getItem('savedTransferCode')) {
      // Clear saved code if checkbox is unchecked but there was a saved code
      localStorage.removeItem('savedTransferCode');
      setSavedTransferCode("");
    }
    
    const transfers = JSON.parse(localStorage.getItem('transfers') || '[]');
    const newTransfer = {
      recipientNumber: data.recipientNumber,
      amount: data.amount,
      date: new Date().toISOString()
    };
    
    transfers.push(newTransfer);
    localStorage.setItem('transfers', JSON.stringify(transfers));
    
    try {
      await saveTransferToSupabase(newTransfer);
      console.log('Transfer saved to Supabase');
    } catch (error) {
      console.error('Failed to save transfer to Supabase:', error);
    }
    
    // Construct and navigate to tel URL for transfer with encoded USSD
    const telUrl = formatUssdTelUrl(`*169#${data.transferCode}*${data.amount}*${data.recipientNumber}#`);
    window.location.href = telUrl;
    
    setOpenTransferDialog(false);
    transferForm.reset({
      transferCode: data.rememberPassword ? data.transferCode : "",
      amount: "",
      recipientNumber: "",
      rememberPassword: data.rememberPassword
    });
  };

  return (
    <PageLayout 
      title={t('tools.transfer.title')} 
      logo={logo}
      showBackButton
      colorScheme="bg-gradient-to-r from-teal-500 to-cyan-500"
    >
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-4">
              <Send className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('tools.transfer.title')}</h2>
            <p className="text-gray-600">{t('tools.transfer.description')}</p>
          </div>

          <Button 
            onClick={() => setOpenTransferDialog(true)}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium py-3 rounded-lg transition-all"
          >
            {t('tools.transfer.dialogTitle')}
          </Button>
        </div>

        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-100">
          <h3 className="font-medium text-teal-800 mb-2">Comment ça marche ?</h3>
          <ul className="text-sm text-teal-700 space-y-1">
            <li>• Entrez votre code de transfert personnel</li>
            <li>• Saisissez le montant à transférer</li>
            <li>• Indiquez le numéro du destinataire</li>
            <li>• Confirmez le transfert</li>
          </ul>
        </div>
      </div>

      <Dialog open={openTransferDialog} onOpenChange={setOpenTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('tools.transfer.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <Form {...transferForm}>
            <form onSubmit={transferForm.handleSubmit(onTransferSubmit)} className="space-y-4">
              <FormField
                control={transferForm.control}
                name="transferCode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input 
                        placeholder={t('tools.transfer.codePlaceholder')} 
                        {...field} 
                        type="password"
                        className="text-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={transferForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input 
                        placeholder={t('tools.transfer.amountPlaceholder')} 
                        {...field} 
                        type="text"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        className="text-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={transferForm.control}
                name="recipientNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input 
                        placeholder={t('tools.transfer.recipientPlaceholder')} 
                        {...field} 
                        type="tel"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        className="text-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={transferForm.control}
                name="rememberPassword"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="text-sm font-medium">{t('tools.transfer.rememberPassword')}</div>
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setOpenTransferDialog(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit">{t('common.continue')}</Button>
              </DialogFooter>
            </form>
          </Form>
          <p className="text-sm text-muted-foreground mt-2">
            {t('tools.transfer.hint')}
          </p>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default TransferCredit;
